<template>
  <div
    class="moveTable"
    @keydown.left="canNavigate && gotoMove(moveIndex - 1)"
    @keydown.right="
      canNavigate &&
        moves &&
        moveIndex < moves.length - 1 &&
        gotoMove(moveIndex + 1)
    "
  >
    <!-- Control header -->
    <div
      v-if="canNavigate"
      class="movesTable__header card is-flex is-justify-content-space-between"
      style="padding: 5px;"
    >
      <button class="button" @click="gotoMove(-3)" :disabled="moveIndex < 0">
        <span class="icon is-small fas fa-fast-backward"></span>
      </button>
      <button
        class="button"
        @click="gotoMove(moveIndex - 1)"
        :disabled="moveIndex < 0"
      >
        <span class="icon is-small fas fa-step-backward"></span>
      </button>
      <button
        class="button is-info"
        :class="autoplayDelay === 1000 ? '' : 'is-outlined'"
        @click="autoplay(1000)"
        :disabled="!fullMoveCount"
      >
        <span
          class="icon is-small fas"
          :class="autoplayDelay === 1000 ? 'fa-pause' : 'fa-play'"
        ></span>
      </button>
      <button
        class="button is-info"
        :class="autoplayDelay === 500 ? '' : 'is-outlined'"
        @click="autoplay(500)"
        :disabled="!fullMoveCount"
      >
        <span
          class="icon is-small fas"
          v-bind:class="autoplayDelay === 500 ? 'fa-pause' : 'fa-forward'"
        ></span>
      </button>
      <button
        class="button"
        @click="gotoMove(moveIndex + 1)"
        :disabled="!moves || moveIndex >= moves.length - 1"
      >
        <span class="icon is-small fas fa-step-forward"></span>
      </button>
      <button
        class="button"
        @click="gotoMove(moves.length - 1)"
        :disabled="!moves || moveIndex >= moves.length - 1"
      >
        <span class="icon is-small fas fa-fast-forward"></span>
      </button>
    </div>

    <!-- Static header -->
    <table v-else style="width: 100%;" class="movesTable__header">
      <b-loading :is-full-page="false" :active="isLoading"></b-loading>
      <thead>
        <tr>
          <th style="width: 20%" class="movesTable__header">Move</th>
          <th style="width: 40%" class="movesTable__header">White</th>
          <th style="width: 40%" class="movesTable__header">Black</th>
        </tr>
      </thead>
    </table>

    <!-- List of moves -->
    <table style="width: 100%;">
      <b-loading :is-full-page="false" :active="isLoading"></b-loading>
      <tbody v-if="moves && match">
        <tr v-for="(n, i) in fullMoveCount" :key="i">
          <td style="width: 20%">{{ i + 1 }}</td>
          <td style="width: 40%">
            <MoveButton
              v-if="whiteMoves.length && whiteMoves[i]"
              :move="whiteMoves[i]"
              :isFocused="whiteMoves[i].moveIndex === moveIndex"
              :isPlaying="!match.result && playerColor"
              @onClick="gotoMove"
            />
          </td>
          <td>
            <MoveButton
              v-if="blackMoves.length && blackMoves[i]"
              :move="blackMoves[i]"
              :isFocused="blackMoves[i].moveIndex === moveIndex"
              :isPlaying="!match.result && playerColor"
              @onClick="gotoMove"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <div class="movesTable__footer">
      <div v-if="match.result" class="movesTable__result">
        <p>
          <strong>{{ scoreText() }}</strong>
        </p>
        <em>{{ resultText() }}</em>
      </div>
    </div>
  </div>
</template>

<script>
import MoveButton from "./MoveButton.vue";
import GameHelper from "@/utils/game-helper";
import MoveHelper from "@/utils/move-helper";

export default {
  components: { MoveButton },
  name: "movesTable",

  props: ["moves", "match", "playerColor", "blackToStart"],

  data() {
    return {
      moveIndex: -1,
      replay: false,
      isAutoplaying: false,
      timerInterval: null,
      autoplayDelay: null,
    };
  },

  computed: {
    isLoading() {
      return !this.moves;
    },

    canNavigate() {
      return this.match && (!this.playerColor || this.match.result);
    },

    fullMoveCount() {
      return MoveHelper.fullMoveCount(this.moves, this.blackToStart);
    },

    blackMoves() {
      return MoveHelper.blackMoves(this.match, this.moves, this.blackToStart);
    },

    whiteMoves() {
      return MoveHelper.whiteMoves(this.match, this.moves, this.blackToStart);
    },
  },

  watch: {
    moves: {
      immediate: true,
      handler(moves) {
        if (!moves || !moves.length) {
          this.moveIndex = -3; // to avoid cases of moves.length is indeed 0
          this.gotoMove(-3);
          return;
        }
        if (this.replay && !this.isAutoplaying) {
          this.autoplay(1000);
          return;
        }

        // Check createdAt to avoid case of new move and firebase didn't return createdAt timestamp
        if (!this.isAutoplaying && moves[moves.length - 1].createdAt) {
          this.gotoMove(moves.length - 1, false);
        }
      },
    },

    match: function() {},
  },

  methods: {
    gotoMove(moveIndex, loadMove = true) {
      // console.log("gotoMove", moveIndex, loadMove);
      this.$emit("scrollTo", moveIndex);
      if (loadMove) this.$emit("onLoadMove", moveIndex);

      if (moveIndex < this.moveIndex) this.stopAutoplay();
      this.moveIndex = Math.max(-1, moveIndex);
      if (!this.moves) return;
      if (moveIndex >= this.moves.length) {
        this.stopAutoplay();
        this.moveIndex = this.moves.length - 1;
      }
    },

    autoplay(delay) {
      if (!this.moves || !this.moves.length) return;
      if (this.isAutoplaying && this.autoplayDelay === delay) {
        return this.stopAutoplay();
      }
      clearInterval(this.timerInterval);
      this.isAutoplaying = true;
      this.autoplayDelay = delay;

      if (this.moveIndex >= this.moves.length - 1) this.gotoMove(0);
      this.timerInterval = setInterval(() => {
        this.gotoMove(this.moveIndex + 1);
      }, delay);
    },

    stopAutoplay() {
      clearInterval(this.timerInterval);
      this.isAutoplaying = false;
      this.timerInterval = null;
      this.autoplayDelay = null;
    },

    scoreText() {
      return GameHelper.scoreText(
        this.match.player_1_score,
        this.match.player_2_score
      );
    },

    resultText() {
      return GameHelper.resultText(
        this.match.player_1_score,
        this.match.player_2_score,
        this.match.result
      );
    },
  },

  created() {
    this.replay = !!this.$route.query.replay;
  },
};
</script>

<style scoped>
table {
  position: relative;
  border-collapse: collapse;
}

tbody {
  overflow: scroll;
}

th,
td {
  padding: 0.25rem;
  vertical-align: bottom;
}

.movesTable__header {
  position: sticky;
  top: 0; /* Don't forget this, required for the stickiness */
  z-index: 1;
  background-color: #b7dcff;
}

.movesTable__footer {
  padding-bottom: 13rem;
}

.movesTable__result {
  border: 1px solid lightgray;
}
</style>
